import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { DATE_FORMAT, DomUtil } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { CheckCircleThinIcon, Clock3Icon } from '@root/shared/ui-kit'

import BG_STRIPES from '../assets/bg-stripes.inline.svg'

import type { SupportTicket } from '../model/support-ticket'

type SupportTicketsItemProps = {
  ticket: SupportTicket
  onSelect: () => void
}

export const SupportTicketsItemAdmin: React.FC<SupportTicketsItemProps> = ({
  onSelect,
  ticket,
}) => {
  const { t } = useTranslation()
  const { lastMessagePreview, createdAt, subject } = ticket

  const [status] = useAtom(ticket.status)
  const Icon = status === 'active' ? OpenTicketIcon : ClosedTicketIcon

  return (
    <Root onClick={onSelect} data-status={status}>
      <Heading>
        <Icon size={12} />
        <Title ellipsis>{subject}</Title>
        <DateTime>
          <AuthorInfo>
            {t('common.by')}:
            <UserShortAvatar
              userShort={ticket.author}
              size={16}
              outlineSpacingWidth={0.5}
              withHypedLetter
              isOnline={ticket.author.isOnline}
            />
            <AuthorName>{UserShort.someName(ticket.author)}</AuthorName>
          </AuthorInfo>
          {DateF.format(createdAt, DATE_FORMAT.dd_MM_yy)}{' '}
          <span>{DateF.format(createdAt, DATE_FORMAT.HH_mm)}</span>
        </DateTime>
      </Heading>
      <Description>{lastMessagePreview}</Description>
    </Root>
  )
}

const Root = styled.div`
  background: rgba(217, 217, 217, 0.03);

  min-height: 56px;
  border-radius: 10px;
  margin-bottom: 4px;
  padding: 8px;

  display: grid;
  gap: 8px;
  cursor: pointer;

  transition: background 0.15s ease-in-out;

  &[data-status='active'] {
    background-image: ${DomUtil.cssUrl(BG_STRIPES)};
    border: 1px solid var(--backgroundA-8);
  }
  &:hover {
    background: rgba(217, 217, 217, 0.07);
  }
`

const Title = styled(Typography)`
  font-size: 12px;
  font-weight: 600;

  min-width: 0;
  margin-inline-end: 10px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 4px;
`

const Description = styled.div`
  color: var(--text-11);

  font-size: 12px;
  font-weight: 500;
`

const AuthorInfo = styled.div`
  color: var(--text-10);

  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  font-size: 11px;
  font-weight: 400;
`

const AuthorName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DateTime = styled.div`
  color: var(--text-10);

  text-align: right;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;

  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;

  gap: 4px;
`

const OpenTicketIcon = styled(Clock3Icon)`
  color: var(--amber-9);
`

const ClosedTicketIcon = styled(CheckCircleThinIcon)`
  color: var(--green-9);
`
